import { useEffect, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { RefreshCw, RotateCcw, TriangleAlert } from 'lucide-react';
import { adminQueries, getErrorMessage, hasProblemType, keys } from '@blimo/api-client';
import { useApi } from '@blimo/api-client/react';
import { mailTemplateSchema } from '@blimo/shared';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  applyProblemToForm,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ConfirmDialog,
  ErrorState,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormRootError,
  HtmlEditor,
  HtmlFrame,
  Input,
  insertAtCursor,
  PageHeader,
  Skeleton,
  toast,
} from '@blimo/ui';
import { DetailSkeleton } from '../../../components/data.jsx';

export function MailTemplateEditorPage() {
  const api = useApi();
  const { templateKey } = useParams({ strict: false });
  const { data: template, error, isPending, refetch } = useQuery(adminQueries.mailTemplate(api, templateKey));
  const breadcrumb = (
    <Link to="/kommunikation/vorlagen" className="hover:underline">
      Vorlagen
    </Link>
  );
  if (error) {
    return (
      <>
        <PageHeader title="Vorlage" breadcrumb={breadcrumb} />
        <ErrorState error={error} onRetry={() => refetch()} notFoundTitle="Vorlage nicht gefunden" />
      </>
    );
  }
  if (isPending) return <DetailSkeleton />;
  return (
    <>
      <PageHeader
        breadcrumb={breadcrumb}
        title={template.label}
        description={
          <span className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-xs">{template.key}</span>
            {template.customized ? <Badge variant="info">Angepasst</Badge> : <Badge variant="neutral">Standardtext</Badge>}
          </span>
        }
      />
      {/* key: a reset/save replaces the server values – start the form fresh from them */}
      <TemplateEditor key={`${template.customized}-${template.subject}-${template.body_html}`} template={template} />
    </>
  );
}

function TemplateEditor({ template }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const subjectRef = useRef(/** @type {HTMLInputElement|null} */ (null));
  const bodyRef = useRef(/** @type {HTMLTextAreaElement|null} */ (null));
  const lastFocused = useRef(/** @type {'subject'|'body_html'} */ ('body_html'));
  const [unknown, setUnknown] = useState(/** @type {string[]|null} */ (null));
  const [resetOpen, setResetOpen] = useState(false);
  const form = useForm({ resolver: zodResolver(mailTemplateSchema), defaultValues: { subject: template.subject ?? '', body_html: template.body_html ?? '' } });
  const draft = useDebouncedDraft(form, template);

  function insertPlaceholder(name) {
    const field = lastFocused.current;
    const el = field === 'subject' ? subjectRef.current : bodyRef.current;
    const token = `{{ ${name} }}`;
    if (!el) {
      form.setValue(field, `${form.getValues(field) ?? ''}${token}`, { shouldDirty: true });
      return;
    }
    insertAtCursor(el, token, (value) => form.setValue(field, value, { shouldDirty: true }));
  }

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: keys.admin.resource('mail-templates') });
  };

  const onSubmit = form.handleSubmit(async (values) => {
    setUnknown(null);
    try {
      await api.admin.mailTemplates.update(template.key, values);
      toast.success('Vorlage gespeichert');
      await refresh();
    } catch (error) {
      if (hasProblemType(error, 'unknown-placeholder')) {
        setUnknown(error.problem?.placeholders ?? (error.detail ? String(error.detail).split(', ') : []));
        return;
      }
      applyProblemToForm(error, form.setError, { fields: ['subject', 'body_html'] });
    }
  });

  async function reset() {
    try {
      await api.admin.mailTemplates.reset(template.key);
      toast.success('Standardtext wiederhergestellt');
      await refresh();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Die Vorlage konnte nicht zurückgesetzt werden.'));
    }
  }

  return (
    <div className="grid gap-3 xl:grid-cols-2">
      <Card className="min-w-0">
        <CardHeader>
          <CardTitle as="h2">Bearbeiten</CardTitle>
          <CardDescription>Platzhalter werden beim Versand durch die echten Werte ersetzt.</CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
              <FormRootError />
              {unknown && (
                <Alert variant="destructive" role="alert">
                  <TriangleAlert aria-hidden="true" />
                  <div className="flex flex-col gap-1">
                    <AlertTitle>Unbekannte Platzhalter</AlertTitle>
                    <AlertDescription>
                      Diese Platzhalter gibt es für die Vorlage nicht:{' '}
                      <span aria-label="Unbekannte Platzhalter">
                        {unknown.map((p) => (
                          <code key={p} className="mr-1 rounded bg-card px-1 font-mono text-xs">{`{{ ${p} }}`}</code>
                        ))}
                      </span>
                    </AlertDescription>
                  </div>
                </Alert>
              )}
              <div className="flex flex-col gap-1.5">
                <span className="text-sm font-medium">Platzhalter</span>
                <div role="group" aria-label="Platzhalter einfügen" className="flex flex-wrap gap-1">
                  {(template.placeholders ?? []).map((p) => (
                    <button
                      key={p}
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => insertPlaceholder(p)}
                      className="rounded-md border bg-muted px-2 py-0.5 font-mono text-xs hover:bg-accent"
                      aria-label={`Platzhalter ${p} einfügen`}
                    >
                      {`{{ ${p} }}`}
                    </button>
                  ))}
                  {!template.placeholders?.length && <span className="text-sm text-muted-foreground">Keine Platzhalter.</span>}
                </div>
              </div>
              <FormField
                name="subject"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Betreff</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        ref={(el) => {
                          field.ref(el);
                          subjectRef.current = el;
                        }}
                        onFocus={() => (lastFocused.current = 'subject')}
                        autoComplete="off"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                name="body_html"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Text</FormLabel>
                    <FormControl>
                      <HtmlEditor
                        label="Text"
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        rows={16}
                        textareaRef={bodyRef}
                        onTextareaFocus={() => (lastFocused.current = 'body_html')}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex flex-wrap gap-2">
                <Button type="submit" loading={form.formState.isSubmitting}>
                  Speichern
                </Button>
                {template.customized && (
                  <Button type="button" variant="outline" onClick={() => setResetOpen(true)}>
                    <RotateCcw aria-hidden="true" /> Auf Standard zurücksetzen
                  </Button>
                )}
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
      <PreviewCard templateKey={template.key} draft={draft} />
      <ConfirmDialog
        open={resetOpen}
        onOpenChange={setResetOpen}
        title="Auf Standardtext zurücksetzen?"
        description="Der eigene Betreff und Text werden verworfen; es gilt wieder der Standardtext."
        confirmLabel="Zurücksetzen"
        destructive
        onConfirm={reset}
      />
    </div>
  );
}

const PREVIEW_DELAY = 400;

/**
 * Unsaved subject/body of the form (debounced) – or `undefined` while the form equals the stored version,
 * so that the preview of the stored template is shown.
 */
function useDebouncedDraft(form, template) {
  const subject = useWatch({ control: form.control, name: 'subject' });
  const body = useWatch({ control: form.control, name: 'body_html' });
  const [draft, setDraft] = useState(/** @type {{ subject: string, body_html: string } | undefined} */ (undefined));
  useEffect(() => {
    const changed = subject !== (template.subject ?? '') || body !== (template.body_html ?? '');
    const timer = setTimeout(() => setDraft(changed ? { subject, body_html: body } : undefined), PREVIEW_DELAY);
    return () => clearTimeout(timer);
  }, [subject, body, template.subject, template.body_html]);
  return draft;
}

/** Preview with sample values (POST …/preview): the unsaved draft while editing, otherwise the stored template. */
function PreviewCard({ templateKey, draft }) {
  const api = useApi();
  const preview = useQuery(adminQueries.mailTemplatePreview(api, templateKey, draft));
  return (
    <Card className="min-w-0">
      <CardHeader className="flex flex-row items-start justify-between gap-2">
        <div className="flex flex-col gap-1">
          <CardTitle as="h2">Vorschau</CardTitle>
          <CardDescription data-testid="preview-source">
            {draft
              ? 'Ungespeicherter Entwurf – mit Beispielwerten in eckigen Klammern. Erst „Speichern“ übernimmt die Änderungen.'
              : 'Gespeicherte Fassung – mit Beispielwerten in eckigen Klammern.'}
          </CardDescription>
        </div>
        <Button type="button" size="sm" variant="outline" onClick={() => preview.refetch()} loading={preview.isFetching}>
          <RefreshCw aria-hidden="true" /> Aktualisieren
        </Button>
      </CardHeader>
      <CardContent className="flex flex-col gap-2">
        {preview.error ? (
          <ErrorState compact error={preview.error} onRetry={() => preview.refetch()} />
        ) : preview.isPending ? (
          <Skeleton className="h-64 w-full" />
        ) : (
          <>
            <p className="text-sm">
              <span className="text-muted-foreground">Betreff: </span>
              <span className="font-medium" data-testid="preview-subject">
                {preview.data.subject}
              </span>
            </p>
            <HtmlFrame html={preview.data.body_html} title="Vorschau der Vorlage" minHeight={280} />
          </>
        )}
      </CardContent>
    </Card>
  );
}
